import { AnimatePresence, motion } from 'motion/react';
import { ONBOARDING_ANALYZING_PROGRESS_TRACK_CLASS } from '../constants';
import { useOnboardingAnalysisMessages } from '../hooks/useOnboardingAnalysisMessages';

interface OnboardingAnalyzingStateProps {
  selectionKey: string;
  messages: readonly string[];
  settleMs: number;
  messageIntervalMs?: number;
  /** False when the panel is rendered only to reserve height (result is showing). */
  isActive?: boolean;
}

function AnalyzingRadarGraphic() {
  return (
    <div className="relative flex h-9 w-9 shrink-0 items-center justify-center" aria-hidden="true">
      <motion.span
        className="absolute inset-0 rounded-full border border-emerald-300/50"
        animate={{ scale: [1, 1.18, 1], opacity: [0.55, 0.15, 0.55] }}
        transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.span
        className="absolute h-6 w-6 rounded-full border border-dashed border-emerald-400/70"
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
      />
      <motion.span
        className="absolute h-4 w-4 origin-bottom rounded-full bg-gradient-to-t from-emerald-500/25 to-transparent"
        style={{ clipPath: 'polygon(50% 100%, 0 0, 100% 0)' }}
        animate={{ rotate: 360 }}
        transition={{ duration: 2.2, repeat: Infinity, ease: 'linear' }}
      />
      <span className="relative h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.65)]" />
    </div>
  );
}

export default function OnboardingAnalyzingState({
  selectionKey,
  messages,
  settleMs,
  messageIntervalMs,
  isActive = true,
}: OnboardingAnalyzingStateProps) {
  const { message } = useOnboardingAnalysisMessages({
    messages,
    intervalMs: messageIntervalMs,
    enabled: isActive,
    resetKey: selectionKey,
  });

  return (
    <div className="flex w-full flex-col justify-center">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-gray-50 ring-1 ring-gray-200/80">
          <AnalyzingRadarGraphic />
        </span>

        <div className="min-w-0 flex-1">
          <AnimatePresence mode="wait" initial={false}>
            <motion.p
              key={message}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -5 }}
              transition={{ duration: 0.2 }}
              className="text-sm font-semibold leading-snug text-gray-900"
            >
              {message}
            </motion.p>
          </AnimatePresence>
        </div>
      </div>

      <div className={ONBOARDING_ANALYZING_PROGRESS_TRACK_CLASS}>
        <div
          key={selectionKey}
          className="relative h-full rounded-full bg-gradient-to-r from-emerald-400 via-emerald-500 to-teal-500"
          style={{
            width: '6%',
            animation: `onboardingAnalyzingProgress ${settleMs}ms ease-out forwards`,
          }}
        >
          <span className="onboarding-analyzing-shimmer absolute inset-y-0 w-1/3 rounded-full" />
        </div>
      </div>
    </div>
  );
}
